import type { VisibilityCondition, DataSourceType } from "../schema/visibility";
import {
  isLeafCondition,
  isAndCondition,
  isOrCondition,
  isNotCondition,
  isElemMatchCondition,
} from "../schema/visibility";

/**
 * Extracts unique data source types from a visibility condition tree.
 * Traverses the entire tree recursively to collect all sources.
 *
 * @param condition - The visibility condition tree to analyze
 * @returns Set of unique DataSourceType values needed for evaluation
 */
export function extractDataSources(condition: VisibilityCondition): Set<DataSourceType> {
  const sources = new Set<DataSourceType>();
  collectSources(condition, sources);
  return sources;
}

/**
 * Internal recursive helper to collect sources from condition tree.
 */
function collectSources(condition: VisibilityCondition, sources: Set<DataSourceType>): void {
  if (isLeafCondition(condition)) {
    sources.add(condition.source);
    return;
  }

  if (isAndCondition(condition)) {
    condition.$and.forEach((c) => collectSources(c, sources));
    return;
  }

  if (isOrCondition(condition)) {
    condition.$or.forEach((c) => collectSources(c, sources));
    return;
  }

  if (isNotCondition(condition)) {
    collectSources(condition.$not, sources);
    return;
  }

  if (isElemMatchCondition(condition)) {
    sources.add(condition.$elemMatch.source);
  }
}

/**
 * Extracts data sources from multiple blocks' visibility conditions.
 * Useful for page-level prefetching.
 *
 * @param blocks - Array of blocks with optional visibility conditions
 * @returns Set of unique DataSourceType values needed across all blocks
 */
export function extractDataSourcesFromBlocks(
  blocks: Array<{ visibility?: { condition: VisibilityCondition } }>
): Set<DataSourceType> {
  const sources = new Set<DataSourceType>();

  for (const block of blocks) {
    if (block.visibility?.condition) {
      const blockSources = extractDataSources(block.visibility.condition);
      blockSources.forEach((s) => sources.add(s));
    }
  }

  return sources;
}
